import {
  registerNativeIndicator,
  type Background,
  type DrawingLabel,
  type DrawingLine,
  type InputSchema,
  type InputValue,
  type NativeIndicator,
  type NativeIndicatorContext,
  type PriceLine,
  type SeriesPoint,
  type SeriesSpec,
} from "@luxalgo/vela";
import { regimeOverlayFillRgba } from "../marketConditionRegimeColors";
import {
  resolveOverlayAdjustedTradePnl,
  resolveTradePnlRealizationTime,
} from "../../views/Dashboard/TradeLog/components/TradeReview/executionCandlesPnlOverlay";
import { executionCandleTimeToMillis } from "./executionCandlesToVelaBars";
import { emitTesseraConceptOverlays } from "./tesseraConceptOverlayEmit";
import { destackMaEndLabels } from "./tesseraMaEndLabels";
import { emitPatternBarHighlights } from "./tesseraPatternBarHighlight";
import { emitTesseraTradeDrawings } from "./tesseraTradeMarkerEmit";
import { STRATEGY_RD_MA_SPECS } from "../strategyRdConceptIndicators";
import type { TesseraLaneBOverlayId } from "../../types/executionCandlesOverlays";
import {
  STRATEGY_RD_CHIP_OSC_VALUE_FIELD as OSC_VALUE_FIELD,
  type StrategyRdChipNativeSpec,
} from "../strategyRdChartChips";
export {
  STRATEGY_RD_CHIP_REPLACED_TYPES,
  STRATEGY_RD_CHIP_TYPE_PREFIX,
  strategyRdChipNativeSpecs,
  type StrategyRdChipNativeSpec,
} from "../strategyRdChartChips";
import {
  STRATEGY_RD_MAX_SUB_PANELS,
  strategyRdSeriesEndLabel,
  strategyRdSeriesLineLabel,
  strategyRdSeriesLineStyle,
  strategyRdSqueezeLegend,
  type StrategyRdChartSeries,
} from "../strategyRdIndicatorSeries";
import {
  collapseLabelSpans,
  getTesseraOverlayState,
  subscribeTesseraOverlayState,
  type TesseraOverlayChartState,
} from "./tesseraOverlayStore";

/** Keep Lane B fills subtle so grey candles + trade markers stay readable. */
const DIRECTION_COLORS: Record<string, string> = {
  up: "rgba(6, 227, 99, 0.08)",
  down: "rgba(224, 1, 1, 0.08)",
  flat: "rgba(107, 114, 128, 0.06)",
  unknown: "rgba(69, 90, 100, 0.05)",
};

const SESSION_COLORS: Record<string, string> = {
  london: "rgba(56, 187, 209, 0.07)",
  new_york: "rgba(115, 230, 240, 0.07)",
  london_new_york: "rgba(62, 234, 236, 0.08)",
  tokyo: "rgba(56, 187, 209, 0.06)",
  sydney: "rgba(150, 251, 247, 0.06)",
  sydney_tokyo: "rgba(115, 230, 240, 0.07)",
  asian: "rgba(56, 187, 209, 0.06)",
};

const BUCKET_COLORS: Record<string, string> = {
  weak: "rgba(107, 114, 128, 0.07)",
  developing: "rgba(115, 230, 240, 0.07)",
  strong: "rgba(6, 227, 99, 0.07)",
  very_strong: "rgba(6, 227, 99, 0.09)",
  oversold: "rgba(6, 227, 99, 0.08)",
  neutral: "rgba(107, 114, 128, 0.06)",
  overbought: "rgba(224, 1, 1, 0.08)",
  very_low: "rgba(107, 114, 128, 0.06)",
  low: "rgba(115, 230, 240, 0.06)",
  medium: "rgba(56, 187, 209, 0.07)",
  high: "rgba(224, 1, 1, 0.07)",
  very_high: "rgba(224, 1, 1, 0.09)",
  tight_squeeze: "rgba(139, 92, 246, 0.08)",
  normal: "rgba(107, 114, 128, 0.05)",
  expanded: "rgba(224, 1, 1, 0.07)",
  lower_extreme: "rgba(6, 227, 99, 0.08)",
  lower: "rgba(6, 227, 99, 0.06)",
  middle: "rgba(107, 114, 128, 0.05)",
  upper: "rgba(224, 1, 1, 0.06)",
  upper_extreme: "rgba(224, 1, 1, 0.08)",
  lower_quartile: "rgba(6, 227, 99, 0.07)",
  upper_quartile: "rgba(224, 1, 1, 0.07)",
};

let activeStoreKey = "";

export function setTesseraNativeIndicatorsStoreKey(key: string): void {
  activeStoreKey = key;
}

function readState(): TesseraOverlayChartState | null {
  if (!activeStoreKey) return null;
  return getTesseraOverlayState(activeStoreKey);
}

function emptySchema(): InputSchema[] {
  return [
    {
      key: "revision",
      title: "Revision",
      type: "int",
      defval: 0,
    },
  ];
}

function makeLabelBackgroundIndicator(opts: {
  type: string;
  title: string;
  shortTitle: string;
  laneId: string;
  colorFor: (label: string) => string;
  getConditions: (state: TesseraOverlayChartState) => Array<string | null> | null | undefined;
}): void {
  registerNativeIndicator({
    type: opts.type,
    title: opts.title,
    shortTitle: opts.shortTitle,
    paneHint: "price",
    overlay: true,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;

      const compute = () => {
        if (!ctx) return;
        const state = readState();
        // Visibility is owned by the chart Indicators panel (instance mounted or not).
        // Paint whenever overlay data is present.
        if (!state) {
          ctx.emit({ backgrounds: [] });
          return;
        }
        const conditions = opts.getConditions(state);
        if (!conditions?.length || !state.barTimesMs.length) {
          ctx.emit({ backgrounds: [] });
          return;
        }
        const spans = collapseLabelSpans(state.barTimesMs, conditions);
        const backgrounds: Background[] = spans.map((span, idx) => ({
          id: `${opts.type}-${idx}`,
          paneId: "price",
          from: span.from,
          to: span.to,
          color: opts.colorFor(span.label),
          overlay: true,
        }));
        ctx.emit({ backgrounds });
      };

      return {
        start(c) {
          ctx = c;
          unsub?.();
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars() {
          compute();
        },
        onViewport() {},
        setInputs() {
          compute();
        },
        suspend() {},
        resume() {
          compute();
        },
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });
}

function pointsFromValues(
  barTimesMs: readonly number[],
  values: readonly (number | null | undefined)[]
): SeriesPoint[] {
  const n = Math.min(barTimesMs.length, values.length);
  const points: SeriesPoint[] = [];
  for (let i = 0; i < n; i += 1) {
    const v = values[i];
    points.push({
      time: barTimesMs[i]!,
      value: v != null && Number.isFinite(Number(v)) ? Number(v) : null,
    });
  }
  return points;
}

function lastFinitePoint(
  barTimesMs: readonly number[],
  values: readonly (number | null | undefined)[]
): { timeMs: number; value: number } | null {
  const n = Math.min(barTimesMs.length, values.length);
  for (let i = n - 1; i >= 0; i -= 1) {
    const v = values[i];
    if (v != null && Number.isFinite(Number(v))) {
      return { timeMs: barTimesMs[i]!, value: Number(v) };
    }
  }
  return null;
}

function makeLineSeries(
  id: string,
  title: string,
  points: SeriesPoint[],
  color: string,
  paneId: string,
  overlay: boolean
): SeriesSpec {
  return {
    id,
    title,
    paneId,
    kind: "line",
    points,
    overlay,
    style: { color, width: 2, lineStyle: "solid" },
  };
}

/**
 * Register Tessera Lane B native indicators.
 * Always overwrites the registry so HMR / style fixes take effect.
 * Call before mounting VelaWorkspace.
 */
/** MA lane lines (+ end labels) for the lanes `has` accepts — shared by the MA report and Lab chips. */
function maLaneOutput(
  state: TesseraOverlayChartState,
  series: NonNullable<NonNullable<TesseraOverlayChartState["overlays"]>["price"]>["series"],
  has: (lane: string) => boolean
): { series: SeriesSpec[]; labels: DrawingLabel[] } {
  const out: SeriesSpec[] = [];
  const endLabels: DrawingLabel[] = [];
  STRATEGY_RD_MA_SPECS.forEach((spec) => {
    if (!has(spec.id)) return;
    const values = series.map((p) => {
      const raw = p[spec.field];
      if (raw == null || raw === "") return null;
      const n = typeof raw === "number" ? raw : Number(raw);
      return Number.isFinite(n) ? n : null;
    });
    out.push(
      makeLineSeries(
        `tessera-${spec.id}`,
        spec.title,
        pointsFromValues(state.barTimesMs, values),
        spec.color,
        "price",
        true
      )
    );
    const end = lastFinitePoint(state.barTimesMs, values);
    if (!end) return;
    endLabels.push({
      id: `tessera-${spec.id}-label`,
      paneId: "price",
      xloc: "bar_time",
      x: end.timeMs,
      y: end.value,
      yloc: "price",
      text: spec.title,
      style: "none",
      color: spec.color,
      textColor: spec.color,
      size: "small",
      textAlign: "left",
      fontFamily: "default",
      overlay: true,
      noFill: true,
    });
  });
  return { series: out, labels: destackMaEndLabels(endLabels) };
}

/** Oscillator lane lines (RSI / ADX / ATR / Stoch / MFI) on `paneId` — shared by Osc report and Lab chips. */
function oscLaneOutput(
  state: TesseraOverlayChartState,
  series: NonNullable<NonNullable<TesseraOverlayChartState["overlays"]>["panes"]>["series"],
  has: (lane: string) => boolean,
  paneId: string
): { series: SeriesSpec[]; priceLines: PriceLine[] } {
  const out: SeriesSpec[] = [];
  const priceLines: PriceLine[] = [];
  if (has("rsi")) {
    out.push(
      makeLineSeries(
        "tessera-rsi",
        "RSI 14",
        pointsFromValues(
          state.barTimesMs,
          series.map((p) => p.rsi)
        ),
        "#8B5CF6",
        paneId,
        false
      )
    );
    priceLines.push(
      {
        id: "rsi-30",
        paneId: paneId,
        price: 30,
        color: "rgba(6, 227, 99, 0.5)",
        width: 1,
        lineStyle: "dashed",
      },
      {
        id: "rsi-70",
        paneId: paneId,
        price: 70,
        color: "rgba(224, 1, 1, 0.5)",
        width: 1,
        lineStyle: "dashed",
      }
    );
  }
  if (has("adx")) {
    out.push(
      makeLineSeries(
        "tessera-adx",
        "ADX 14",
        pointsFromValues(
          state.barTimesMs,
          series.map((p) => p.adx)
        ),
        "#F59E0B",
        paneId,
        false
      )
    );
    out.push(
      makeLineSeries(
        "tessera-plus-di",
        "+DI",
        pointsFromValues(
          state.barTimesMs,
          series.map((p) => p.plus_di)
        ),
        "#06E363",
        paneId,
        false
      )
    );
    out.push(
      makeLineSeries(
        "tessera-minus-di",
        "−DI",
        pointsFromValues(
          state.barTimesMs,
          series.map((p) => p.minus_di)
        ),
        "#E00101",
        paneId,
        false
      )
    );
  }
  if (has("atr")) {
    out.push(
      makeLineSeries(
        "tessera-atr-pct",
        "ATR%",
        pointsFromValues(
          state.barTimesMs,
          series.map((p) => p.atr_pct ?? p.atr)
        ),
        "#38BBD1",
        paneId,
        false
      )
    );
  }
  if (has("stoch")) {
    out.push(
      makeLineSeries(
        "tessera-stoch-k",
        "%K",
        pointsFromValues(
          state.barTimesMs,
          series.map((p) => p.stoch_k)
        ),
        "#A78BFA",
        paneId,
        false
      )
    );
    out.push(
      makeLineSeries(
        "tessera-stoch-d",
        "%D",
        pointsFromValues(
          state.barTimesMs,
          series.map((p) => p.stoch_d)
        ),
        "#F59E0B",
        paneId,
        false
      )
    );
    priceLines.push(
      {
        id: "stoch-20",
        paneId: paneId,
        price: 20,
        color: "rgba(6, 227, 99, 0.45)",
        width: 1,
        lineStyle: "dashed",
      },
      {
        id: "stoch-80",
        paneId: paneId,
        price: 80,
        color: "rgba(224, 1, 1, 0.45)",
        width: 1,
        lineStyle: "dashed",
      }
    );
  }
  if (has("mfi")) {
    out.push(
      makeLineSeries(
        "tessera-mfi",
        "MFI",
        pointsFromValues(
          state.barTimesMs,
          series.map((p) => p.mfi)
        ),
        "#2DD4BF",
        paneId,
        false
      )
    );
  }
  return { series: out, priceLines };
}

/** Bollinger lane (upper / middle / lower) over price — shared by BB report and Lab chips. */
function bbLaneOutput(
  state: TesseraOverlayChartState,
  series: NonNullable<NonNullable<TesseraOverlayChartState["overlays"]>["price"]>["series"]
): SeriesSpec[] {
  return [
    makeLineSeries("tessera-bb-upper", "BB upper", pointsFromValues(state.barTimesMs, series.map((p) => p.bb_upper)), "rgba(56, 187, 209, 0.9)", "price", true),
    makeLineSeries("tessera-bb-middle", "BB middle", pointsFromValues(state.barTimesMs, series.map((p) => p.bb_middle)), "rgba(211, 211, 211, 0.9)", "price", true),
    makeLineSeries("tessera-bb-lower", "BB lower", pointsFromValues(state.barTimesMs, series.map((p) => p.bb_lower)), "rgba(56, 187, 209, 0.9)", "price", true),
  ];
}

/** FE MACD lane (line / signal / histogram) on `paneId` — Lab chips. */
function macdLaneOutput(
  state: TesseraOverlayChartState,
  series: NonNullable<NonNullable<TesseraOverlayChartState["overlays"]>["panes"]>["series"],
  paneId: string
): SeriesSpec[] {
  return [
    makeLineSeries("tessera-macd-line", "MACD", pointsFromValues(state.barTimesMs, series.map((p) => p.macd)), "#38BBD1", paneId, false),
    makeLineSeries("tessera-macd-signal", "Signal", pointsFromValues(state.barTimesMs, series.map((p) => p.macd_signal)), "#F59E0B", paneId, false),
    makeLineSeries("tessera-macd-hist", "Hist", pointsFromValues(state.barTimesMs, series.map((p) => p.macd_hist)), "#A78BFA", paneId, false),
  ];
}

/** Applied recipe SL / TP price lines (Strategy Lab) — shared by Rule Levels and the Entry chip. */
function ruleLevelPriceLines(levels: TesseraOverlayChartState["highlightLevels"]): PriceLine[] {
  if (
    !levels ||
    !Number.isFinite(levels.entry) ||
    !Number.isFinite(levels.stop) ||
    !Number.isFinite(levels.target)
  ) {
    return [];
  }
  return [
    { id: "tessera-rule-stop", paneId: "price", price: levels.stop, color: "#E00101", width: 1, lineStyle: "dashed", title: "SL" },
    { id: "tessera-rule-target", paneId: "price", price: levels.target, color: "#06E363", width: 1, lineStyle: "dashed", title: "TP" },
  ];
}

/** Squeeze bars → background spans on one pane. */
function squeezeBackgrounds(
  id: string,
  paneId: string,
  barTimesMs: readonly number[],
  squeezeOn: readonly boolean[] | null | undefined,
  overlay: boolean
): Background[] {
  if (!squeezeOn?.length) return [];
  const labels = barTimesMs.map((_, i) => (squeezeOn[i] ? "on" : null));
  return collapseLabelSpans(barTimesMs, labels)
    .filter((span) => span.label === "on")
    .map((span, idx) => ({
      id: `${id}-squeeze-${idx}`,
      paneId,
      from: span.from,
      to: span.to,
      color: "rgba(139, 92, 246, 0.10)",
      overlay,
    }));
}

/** Series + price lines for one BE indicator-confluence series on `paneId`. */
function strategyRdSeriesSpecs(
  entry: StrategyRdChartSeries,
  barTimesMs: readonly number[],
  paneId: string,
  overlay: boolean,
  /** Chart candles (aligned to barTimesMs) — colour volume bars like the candles. */
  candles?: ReadonlyArray<{ open: number; close: number }> | null
): { series: SeriesSpec[]; priceLines: PriceLine[]; labels: DrawingLabel[] } {
  const series: SeriesSpec[] = [];
  const labels: DrawingLabel[] = [];
  let maIndex = 0;
  entry.lines.forEach((line, i) => {
    const style = strategyRdSeriesLineStyle(line, maIndex);
    if (line.role === "ma") maIndex += 1;
    // Name MAs and min / max distance bands at their right end (like the MA lanes).
    const endText = overlay ? strategyRdSeriesEndLabel(line) : null;
    const end = endText ? lastFinitePoint(barTimesMs, line.values) : null;
    if (endText && end) {
      labels.push({
        id: `tessera-rd-${entry.key}-${i}-label`,
        paneId,
        xloc: "bar_time",
        x: end.timeMs,
        y: end.value,
        yloc: "price",
        text: endText,
        style: "none",
        color: style.color,
        textColor: style.color,
        size: "small",
        textAlign: "left",
        fontFamily: "default",
        overlay: true,
        noFill: true,
      });
    }
    // Mansfield RS: green above 0 (relative strength improving), red below.
    const signColored = entry.conceptId.includes("mansfield") && line.role === "value";
    const points = pointsFromValues(barTimesMs, line.values).map((p) =>
      signColored && p.value != null
        ? { ...p, color: p.value >= 0 ? "#06E363" : "#E00101" }
        : p
    );
    series.push({
      id: `tessera-rd-${entry.key}-${i}`,
      title: strategyRdSeriesLineLabel(entry, line),
      paneId,
      kind: "line",
      points,
      overlay,
      style: { color: style.color, width: style.width, lineStyle: style.lineStyle },
    });
  });
  if (entry.histogram) {
    const isVolume = entry.pane === "volume";
    const points = pointsFromValues(barTimesMs, entry.histogram.values).map((p, i) => {
      if (p.value == null) return { ...p, color: undefined };
      if (!isVolume) {
        // MACD histogram: by sign.
        return { ...p, color: p.value >= 0 ? "rgba(6, 227, 99, 0.55)" : "rgba(224, 1, 1, 0.55)" };
      }
      // Volume: green / red like the chart's own volume (candle close vs open; Up/down
      // volume's close-vs-prior-close sign when that confluence is on). Bars that pass the
      // volume test are solid, the rest faded — no extra colour.
      const candle = candles?.[i];
      const sign =
        entry.barSign?.[i] ??
        (candle && Number.isFinite(candle.open) && Number.isFinite(candle.close)
          ? Math.sign(candle.close - candle.open)
          : null);
      const alpha = entry.passes ? (entry.passes[i] === true ? 0.95 : 0.3) : 0.6;
      const rgb = sign != null && sign < 0 ? "224, 1, 1" : sign != null && sign > 0 ? "6, 227, 99" : "107, 114, 128";
      return { ...p, color: `rgba(${rgb}, ${alpha})` };
    });
    series.push({
      id: `tessera-rd-${entry.key}-hist`,
      title: `${entry.title} · ${entry.histogram.name}`,
      paneId,
      kind: "histogram",
      points,
      overlay,
      style: { color: "rgba(107, 114, 128, 0.5)", width: 1, lineStyle: "solid", base: 0 },
    });
  }
  // Legend key for the shading (a legend-only entry: no points to draw).
  if (entry.squeezeOn?.some(Boolean)) {
    series.push({
      id: `tessera-rd-${entry.key}-squeeze-key`,
      title: `${entry.title} · ${strategyRdSqueezeLegend(entry.conceptId)}`,
      paneId,
      kind: "line",
      points: barTimesMs.map((time) => ({ time, value: null })),
      overlay,
      style: { color: "rgba(139, 92, 246, 0.55)", width: 1, lineStyle: "solid" },
    });
  }
  const priceLines: PriceLine[] = entry.levels.map((level, i) => ({
    id: `tessera-rd-${entry.key}-level-${i}`,
    paneId,
    price: level.value,
    color: "rgba(107, 114, 128, 0.6)",
    width: 1,
    lineStyle: "dashed",
    title: level.name && level.name !== "zero" ? level.name : undefined,
  }));
  return { series, priceLines, labels };
}

/**
 * Strategy Lab BE indicator series (§1.6c): `tessera-rd-price` draws every price-pane
 * series over the candles; `tessera-rd-sub-N` draws the Nth sub-panel series in its own pane.
 */
function registerStrategyRdSeriesIndicators(): void {
  const makeIndicator = (opts: {
    type: string;
    title: string;
    overlay: boolean;
    pick: (state: TesseraOverlayChartState) => StrategyRdChartSeries[];
  }): void => {
    registerNativeIndicator({
      type: opts.type,
      title: opts.title,
      shortTitle: opts.title,
      paneHint: opts.overlay ? "price" : "new",
      overlay: opts.overlay,
      multiInstance: false,
      inputsSchema: emptySchema,
      defaultInputs: () => ({ revision: 0 }),
      create: (): NativeIndicator => {
        let ctx: NativeIndicatorContext | null = null;
        let unsub: (() => void) | null = null;
        const compute = () => {
          if (!ctx) return;
          const state = readState();
          const entries = state ? opts.pick(state) : [];
          if (!state || entries.length === 0 || !state.barTimesMs.length) {
            ctx.emit({ series: [], priceLines: [], backgrounds: [] });
            return;
          }
          const paneId = opts.overlay ? "price" : opts.type;
          const series: SeriesSpec[] = [];
          const priceLines: PriceLine[] = [];
          const backgrounds: Background[] = [];
          const endLabels: DrawingLabel[] = [];
          for (const entry of entries) {
            const specs = strategyRdSeriesSpecs(
              entry,
              state.barTimesMs,
              paneId,
              opts.overlay,
              ctx.bars()
            );
            series.push(...specs.series);
            priceLines.push(...specs.priceLines);
            endLabels.push(...specs.labels);
            backgrounds.push(
              ...squeezeBackgrounds(
                `tessera-rd-${entry.key}`,
                paneId,
                state.barTimesMs,
                entry.squeezeOn,
                opts.overlay
              )
            );
          }
          // Concept markers aimed at this panel (e.g. MACD "Cross", ATR regime rank).
          let lines: DrawingLine[] = [];
          let labels: DrawingLabel[] = [];
          const own = opts.overlay
            ? []
            : (state.conceptDrawings ?? []).filter((d) => (d.paneId || "").trim() === paneId);
          const first = entries[0]?.lines[0]?.values ?? entries[0]?.histogram?.values ?? [];
          if (own.length && state.barTimesMs.length) {
            const bars = state.barTimesMs.map((t, i) => {
              const v = first[i];
              const y = v != null && Number.isFinite(Number(v)) ? Number(v) : 0;
              return { time: t, open: y, high: y, low: y, close: y, volume: 1 };
            });
            const emitted = emitTesseraConceptOverlays(own, bars);
            lines = emitted.lines;
            labels = emitted.labels;
          }
          ctx.emit({
            series,
            priceLines,
            backgrounds,
            lines,
            labels: [...destackMaEndLabels(endLabels), ...labels],
          });
        };
        return {
          start(c) {
            ctx = c;
            unsub = activeStoreKey ? subscribeTesseraOverlayState(activeStoreKey, compute) : null;
            compute();
          },
          onBars: compute,
          onViewport() {},
          setInputs: compute,
          suspend() {},
          resume: compute,
          stop() {
            unsub?.();
            unsub = null;
            ctx = null;
          },
        };
      },
    });
  };

  makeIndicator({
    type: "tessera-rd-price",
    title: "Confluence lines",
    overlay: true,
    pick: (s) => (s.rdSeries ?? []).filter((e) => e.pane === "price"),
  });
  for (let n = 0; n < STRATEGY_RD_MAX_SUB_PANELS; n += 1) {
    makeIndicator({
      type: `tessera-rd-sub-${n}`,
      title: "Confluence panel",
      overlay: false,
      pick: (s) => {
        const entry = (s.rdSeries ?? []).filter((e) => e.pane !== "price")[n];
        return entry ? [entry] : [];
      },
    });
  }
}

export function ensureTesseraNativeIndicatorsRegistered(): void {
  registerStrategyRdSeriesIndicators();
  makeLabelBackgroundIndicator({
    type: "tessera-regime",
    title: "Tessera Regime (report)",
    shortTitle: "Regime",
    laneId: "regime",
    colorFor: (label) => regimeOverlayFillRgba(label),
    getConditions: (s) =>
      s.overlays?.regime?.conditions ?? s.regime?.conditions ?? null,
  });

  makeLabelBackgroundIndicator({
    type: "tessera-direction",
    title: "Tessera Direction (report)",
    shortTitle: "Direction",
    laneId: "direction",
    colorFor: (label) => DIRECTION_COLORS[label] ?? DIRECTION_COLORS.unknown!,
    getConditions: (s) => s.overlays?.direction?.conditions ?? null,
  });

  makeLabelBackgroundIndicator({
    type: "tessera-direction-short",
    title: "Tessera Short Direction (report)",
    shortTitle: "Dir short",
    laneId: "direction_short",
    colorFor: (label) => DIRECTION_COLORS[label] ?? DIRECTION_COLORS.unknown!,
    getConditions: (s) => s.overlays?.direction_short?.conditions ?? null,
  });

  makeLabelBackgroundIndicator({
    type: "tessera-session",
    title: "Tessera Session (report)",
    shortTitle: "Session",
    laneId: "session",
    colorFor: (label) => SESSION_COLORS[label] ?? "rgba(56, 187, 209, 0.10)",
    getConditions: (s) => s.overlays?.session?.conditions ?? null,
  });

  for (const [type, title, shortTitle, laneId, getter] of [
    [
      "tessera-adx-bucket",
      "Tessera ADX Bucket (report)",
      "ADX bucket",
      "adx_bucket",
      (s: TesseraOverlayChartState) => s.overlays?.adx_bucket?.conditions,
    ],
    [
      "tessera-rsi-bucket",
      "Tessera RSI Bucket (report)",
      "RSI bucket",
      "rsi_bucket",
      (s: TesseraOverlayChartState) => s.overlays?.rsi_bucket?.conditions,
    ],
    [
      "tessera-atr-regime",
      "Tessera ATR Regime (report)",
      "ATR regime",
      "atr_regime",
      (s: TesseraOverlayChartState) => s.overlays?.atr_regime?.conditions,
    ],
    [
      "tessera-squeeze",
      "Tessera BB Squeeze (report)",
      "Squeeze",
      "squeeze",
      (s: TesseraOverlayChartState) => s.overlays?.squeeze?.conditions,
    ],
    [
      "tessera-bb-zone",
      "Tessera BB Zone (report)",
      "BB zone",
      "bb_zone",
      (s: TesseraOverlayChartState) => s.overlays?.bb_zone?.conditions,
    ],
    [
      "tessera-bb-quartile",
      "Tessera BB Quartile (report)",
      "BB quartile",
      "bb_quartile",
      (s: TesseraOverlayChartState) => s.overlays?.bb_quartile?.conditions,
    ],
  ] as const) {
    makeLabelBackgroundIndicator({
      type,
      title,
      shortTitle,
      laneId,
      colorFor: (label) => BUCKET_COLORS[label] ?? "rgba(107, 114, 128, 0.10)",
      getConditions: getter,
    });
  }

  registerNativeIndicator({
    type: "tessera-sma",
    title: "Tessera MA (report)",
    shortTitle: "MA report",
    paneHint: "price",
    overlay: true,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        const state = readState();
        const series = state?.overlays?.price?.series;
        if (!state || !series?.length) {
          ctx.emit({ series: [], labels: [] });
          return;
        }
        const { series: out, labels: maLabels } = maLaneOutput(state, series, (id) => state.enabledLaneB.has(id as TesseraLaneBOverlayId));
        ctx.emit({ series: out, labels: maLabels });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });

  registerNativeIndicator({
    type: "tessera-bb",
    title: "Tessera Bollinger (report)",
    shortTitle: "BB report",
    paneHint: "price",
    overlay: true,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        const state = readState();
        const series = state?.overlays?.price?.series;
        if (!state || !state.enabledLaneB.has("bb") || !series?.length) {
          ctx.emit({ series: [] });
          return;
        }
        ctx.emit({ series: bbLaneOutput(state, series) });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });

  registerNativeIndicator({
    type: "tessera-panes",
    title: "Tessera Oscillators (report)",
    shortTitle: "Osc report",
    paneHint: "new",
    overlay: false,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        const state = readState();
        const series = state?.overlays?.panes?.series;
        if (!state || !series?.length) {
          ctx.emit({ series: [], priceLines: [] });
          return;
        }
        const { series: out, priceLines } = oscLaneOutput(state, series, (id) => state.enabledLaneB.has(id as TesseraLaneBOverlayId), "tessera-panes");
        // Divergence (and other osc-pane) concept drawings — emit here so labels/lines
        // stay on tessera-panes (tessera-concepts remaps onto price).
        const oscConcepts = (state.conceptDrawings ?? []).filter(
          (d) => (d.paneId || "").trim() === "tessera-panes"
        );
        let conceptLines: DrawingLine[] = [];
        let conceptLabels: DrawingLabel[] = [];
        if (oscConcepts.length && state.barTimesMs.length) {
          const rsiBars = state.barTimesMs.map((t, i) => {
            const rsi = series[i]?.rsi;
            const v =
              rsi != null && Number.isFinite(Number(rsi)) ? Number(rsi) : 50;
            return {
              time: t,
              open: v,
              high: v,
              low: v,
              close: v,
              volume: 1,
            };
          });
          const emitted = emitTesseraConceptOverlays(oscConcepts, rsiBars);
          conceptLines = emitted.lines;
          conceptLabels = emitted.labels;
        }
        ctx.emit({
          series: out,
          priceLines,
          lines: conceptLines,
          labels: conceptLabels,
        });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });

  registerNativeIndicator({
    type: "tessera-macd",
    title: "Tessera MACD",
    shortTitle: "MACD",
    paneHint: "new",
    overlay: false,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        const state = readState();
        const series = state?.overlays?.panes?.series;
        if (!state || !state.enabledLaneB.has("macd") || !series?.length) {
          ctx.emit({ series: [] });
          return;
        }
        const macdConcepts = (state.conceptDrawings ?? []).filter(
          (d) => (d.paneId || "").trim() === "tessera-macd"
        );
        let conceptLines: DrawingLine[] = [];
        let conceptLabels: DrawingLabel[] = [];
        if (macdConcepts.length && state.barTimesMs.length) {
          const macdBars = state.barTimesMs.map((t, i) => {
            const macd = series[i]?.macd;
            const v =
              macd != null && Number.isFinite(Number(macd)) ? Number(macd) : 0;
            return {
              time: t,
              open: v,
              high: v,
              low: v,
              close: v,
              volume: 1,
            };
          });
          const emitted = emitTesseraConceptOverlays(macdConcepts, macdBars);
          conceptLines = emitted.lines;
          conceptLabels = emitted.labels;
        }
        ctx.emit({
          series: [
            makeLineSeries(
              "tessera-macd-line",
              "MACD",
              pointsFromValues(
                state.barTimesMs,
                series.map((p) => p.macd)
              ),
              "#38BBD1",
              "tessera-macd",
              false
            ),
            makeLineSeries(
              "tessera-macd-signal",
              "Signal",
              pointsFromValues(
                state.barTimesMs,
                series.map((p) => p.macd_signal)
              ),
              "#F59E0B",
              "tessera-macd",
              false
            ),
            makeLineSeries(
              "tessera-macd-hist",
              "Hist",
              pointsFromValues(
                state.barTimesMs,
                series.map((p) => p.macd_hist)
              ),
              "#A78BFA",
              "tessera-macd",
              false
            ),
          ],
          lines: conceptLines,
          labels: conceptLabels,
        });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });

  registerNativeIndicator({
    type: "tessera-trades",
    title: "Tessera Trades",
    shortTitle: "Trades",
    paneHint: "price",
    overlay: true,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        const state = readState();
        if (!state?.trades?.length) {
          ctx.emit({ labels: [], lines: [], boxes: [] });
          return;
        }
        const { labels, lines, boxes } = emitTesseraTradeDrawings(
          state.trades,
          ctx.bars(),
          state.selectedTradeId,
          {
            showPositionStats: Boolean(state.showPositionStats),
            allPositionTools: Boolean(state.allPositionTools),
          }
        );
        ctx.emit({ labels, lines, boxes });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });

  registerNativeIndicator({
    type: "tessera-pattern-bar",
    title: "Tessera Pattern Bar",
    shortTitle: "Pattern",
    paneHint: "price",
    overlay: true,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        const state = readState();
        if (!state) {
          ctx.emit({ series: [], backgrounds: [], labels: [] });
          return;
        }
        const pathHighlights = (state.pathHighlights ?? []).filter(
          (h) => h != null && Number.isFinite(h.timeMs)
        );
        const timeMs = state.highlightBarTimeMs;
        const highlights =
          pathHighlights.length > 0
            ? pathHighlights
            : timeMs != null && Number.isFinite(timeMs)
              ? [
                  {
                    timeMs,
                    direction: state.highlightDirection ?? null,
                    label: state.highlightLabel,
                  },
                ]
              : [];
        if (highlights.length === 0) {
          ctx.emit({ series: [], backgrounds: [], labels: [] });
          return;
        }
        const velaBars = ctx.bars();
        const times =
          state.barTimesMs.length === velaBars.length && state.barTimesMs.length > 0
            ? state.barTimesMs
            : velaBars.map((b) => b.time);
        const aligned = velaBars.map((bar, i) => ({
          ...bar,
          time: times[i] ?? bar.time,
        }));
        const { series, backgrounds, labels } = emitPatternBarHighlights(highlights, aligned);
        ctx.emit({ series, backgrounds, labels });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });

  registerNativeIndicator({
    type: "tessera-rule-levels",
    title: "Tessera Rule Levels",
    shortTitle: "Levels",
    paneHint: "price",
    overlay: true,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        ctx.emit({ priceLines: ruleLevelPriceLines(readState()?.highlightLevels) });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });

  registerNativeIndicator({
    type: "tessera-sr",
    title: "Tessera S/R Snapshot (report)",
    shortTitle: "S/R",
    paneHint: "price",
    overlay: true,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        const state = readState();
        const sr = state?.overlays?.sr;
        if (!state?.enabledLaneB.has("sr") || !sr) {
          ctx.emit({ priceLines: [], labels: [] });
          return;
        }
        const priceLines: PriceLine[] = [];
        const labels: DrawingLabel[] = [];
        const support = sr.nearest_support != null ? Number(sr.nearest_support) : null;
        const resistance =
          sr.nearest_resistance != null ? Number(sr.nearest_resistance) : null;
        if (support != null && Number.isFinite(support)) {
          priceLines.push({
            id: "tessera-sr-support",
            paneId: "price",
            price: support,
            color: "#06E363",
            width: 1,
            lineStyle: "dotted",
            title: "Support",
          });
        }
        if (resistance != null && Number.isFinite(resistance)) {
          priceLines.push({
            id: "tessera-sr-resistance",
            paneId: "price",
            price: resistance,
            color: "#E00101",
            width: 1,
            lineStyle: "dotted",
            title: "Resistance",
          });
        }
        if (sr.room_bucket && state.barTimesMs.length) {
          const t = state.barTimesMs[Math.floor(state.barTimesMs.length / 2)]!;
          const y =
            resistance != null && support != null
              ? (resistance + support) / 2
              : resistance ?? support ?? 0;
          labels.push({
            id: "tessera-sr-room",
            paneId: "price",
            xloc: "bar_time",
            x: t,
            y,
            yloc: "price",
            text: `S/R ${sr.room_bucket}`,
            style: "label_center",
            color: "rgba(56,187,209,0.85)",
            textColor: "#fff",
            size: "small",
            textAlign: "center",
            fontFamily: "default",
            overlay: true,
          });
        }
        ctx.emit({ priceLines, labels });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });

  registerNativeIndicator({
    type: "tessera-equity",
    title: "Tessera Equity (PnL)",
    shortTitle: "PnL",
    // Own pane + raw $ equity — never on the price candle scale.
    paneHint: "new",
    overlay: false,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        const state = readState();
        if (!state?.trades.length) {
          ctx.emit({ series: [] });
          return;
        }
        const bars = ctx.bars();
        if (!bars.length) {
          ctx.emit({ series: [] });
          return;
        }

        const pnlByTime = new Map<number, number>();
        for (const t of state.trades) {
          const pnl = resolveOverlayAdjustedTradePnl(t, {
            policyOverlayActive: Boolean(state.policyOverlayActive),
            simulationLineOverlay: Boolean(state.simulationLineOverlay),
          });
          if (pnl == null || !Number.isFinite(pnl)) continue;
          const ms = executionCandleTimeToMillis(resolveTradePnlRealizationTime(t));
          if (ms == null || ms < bars[0]!.time) continue;
          let barTime = bars[0]!.time;
          let lo = 0;
          let hi = bars.length - 1;
          while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            const bt = bars[mid]!.time;
            if (bt <= ms) {
              barTime = bt;
              lo = mid + 1;
            } else {
              hi = mid - 1;
            }
          }
          pnlByTime.set(barTime, (pnlByTime.get(barTime) ?? 0) + pnl);
        }
        if (pnlByTime.size === 0) {
          ctx.emit({ series: [] });
          return;
        }

        // Single continuous step series on its own $ scale — per-point green/red.
        let running = 0;
        const points: SeriesPoint[] = [];
        for (let i = 0; i < bars.length; i += 1) {
          const t = bars[i]!.time;
          running += pnlByTime.get(t) ?? 0;
          points.push({
            time: t,
            value: running,
            color: running >= 0 ? "#06E363" : "#E00101",
          });
        }

        ctx.emit({
          series: [
            {
              id: "tessera-equity-line",
              title: "PnL",
              paneId: "tessera-equity",
              kind: "step",
              points,
              overlay: false,
              style: { color: "#06E363", width: 2, lineStyle: "solid", base: 0 },
            },
          ],
          priceLines: [
            {
              id: "tessera-equity-zero",
              paneId: "tessera-equity",
              price: 0,
              color: "rgba(156, 163, 175, 0.55)",
              width: 1,
              lineStyle: "dashed",
            },
          ],
        });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });

  registerNativeIndicator({
    type: "tessera-concepts",
    title: "Tessera Concepts",
    shortTitle: "Concepts",
    paneHint: "price",
    overlay: true,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        const state = readState();
        if (!state?.conceptDrawings?.length) {
          ctx.emit({ boxes: [], labels: [], priceLines: [], lines: [] });
          return;
        }
        const velaBars = ctx.bars();
        const times =
          state.barTimesMs.length === velaBars.length && state.barTimesMs.length > 0
            ? state.barTimesMs
            : velaBars.map((b) => b.time);
        const aligned = velaBars.map((bar, i) => ({
          ...bar,
          time: times[i] ?? bar.time,
        }));
        // Osc-pane drawings emit from tessera-panes; volume is skipped in emit.
        const priceConcepts = state.conceptDrawings.filter((d) => {
          const pane = (d.paneId || "price").trim();
          return pane === "price";
        });
        const { boxes, labels, priceLines, lines } = emitTesseraConceptOverlays(
          priceConcepts,
          aligned
        );
        ctx.emit({ boxes, labels, priceLines, lines });
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey
            ? subscribeTesseraOverlayState(activeStoreKey, compute)
            : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });
}


/** Map Lane B id → Vela native indicator type to add/remove. */
export const LANE_B_TO_NATIVE_TYPE: Record<string, string> = {
  regime: "tessera-regime",
  direction: "tessera-direction",
  direction_short: "tessera-direction-short",
  session: "tessera-session",
  sma5: "tessera-sma",
  sma8: "tessera-sma",
  sma9: "tessera-sma",
  sma10: "tessera-sma",
  sma20: "tessera-sma",
  sma21: "tessera-sma",
  sma30: "tessera-sma",
  sma50: "tessera-sma",
  sma100: "tessera-sma",
  sma200: "tessera-sma",
  ema5: "tessera-sma",
  ema8: "tessera-sma",
  ema9: "tessera-sma",
  ema10: "tessera-sma",
  ema12: "tessera-sma",
  ema20: "tessera-sma",
  ema21: "tessera-sma",
  ema50: "tessera-sma",
  ema100: "tessera-sma",
  ema200: "tessera-sma",
  bb: "tessera-bb",
  adx: "tessera-panes",
  rsi: "tessera-panes",
  atr: "tessera-panes",
  stoch: "tessera-panes",
  mfi: "tessera-panes",
  macd: "tessera-macd",
  rd_price: "tessera-rd-price",
  rd_sub_0: "tessera-rd-sub-0",
  rd_sub_1: "tessera-rd-sub-1",
  rd_sub_2: "tessera-rd-sub-2",
  rd_sub_3: "tessera-rd-sub-3",
  adx_bucket: "tessera-adx-bucket",
  rsi_bucket: "tessera-rsi-bucket",
  atr_regime: "tessera-atr-regime",
  squeeze: "tessera-squeeze",
  bb_zone: "tessera-bb-zone",
  bb_quartile: "tessera-bb-quartile",
  sr: "tessera-sr",
};

/** Always-on Tessera natives (trades, PnL, regime). Toggle via chart Indicators. */
export const TESSERA_CORE_NATIVE_TYPES = [
  "tessera-trades",
  "tessera-equity",
  "tessera-regime",
  "tessera-sr",
  "tessera-pattern-bar",
  "tessera-rule-levels",
] as const;

/**
 * Strategy Lab research charts — concepts around the example bar, not fills.
 * Persist/store are isolated from Trade Review; do not seed report overlays.
 */
export const TESSERA_STRATEGY_RD_CORE_NATIVE_TYPES = [
  "tessera-pattern-bar",
  "tessera-concepts",
  "tessera-trades",
] as const;

/** Force recompute of Tessera natives (bump revision inputs). */
export function bumpTesseraNativeIndicators(
  chart: { indicators: () => Array<{ nativeType?: string; setInput: (k: string, v: InputValue) => void }> },
  revision: number
): void {
  for (const handle of chart.indicators()) {
    if (handle.nativeType?.startsWith("tessera-")) {
      try {
        handle.setInput("revision", revision);
      } catch {
        // ignore
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Strategy Lab: one legend group per recipe chip (Setup / Confluence N / Entry / Volume).
// Types are registered on the fly (Vela can't rename an indicator), named after the chip.
// ---------------------------------------------------------------------------

/** Live spec per registered type — instances read it on every compute (no stale closures). */
const chipSpecByType = new Map<string, { spec: StrategyRdChipNativeSpec; panel: number | null }>();

function chipBarsAligned(ctx: NativeIndicatorContext, state: TesseraOverlayChartState) {
  const velaBars = ctx.bars();
  const times =
    state.barTimesMs.length === velaBars.length && state.barTimesMs.length > 0
      ? state.barTimesMs
      : velaBars.map((b) => b.time);
  return velaBars.map((bar, i) => ({ ...bar, time: times[i] ?? bar.time }));
}

function computeChipMain(ctx: NativeIndicatorContext, type: string): void {
  const found = chipSpecByType.get(type);
  const state = readState();
  const empty = { series: [], labels: [], lines: [], boxes: [], priceLines: [], backgrounds: [] };
  if (!found || !state) {
    ctx.emit(empty);
    return;
  }
  const { spec } = found;
  const series: SeriesSpec[] = [];
  const labels: DrawingLabel[] = [];
  const lines: DrawingLine[] = [];
  const boxes: NonNullable<Parameters<NativeIndicatorContext["emit"]>[0]["boxes"]> = [];
  const priceLines: PriceLine[] = [];
  const backgrounds: Background[] = [];
  const aligned = chipBarsAligned(ctx, state);

  // The chip's own drawings over price (zones, levels, badges, Enter / stop / target…).
  const mine = (state.conceptDrawings ?? []).filter(
    (d) => (d.group ?? "setup") === spec.key && (d.paneId || "price").trim() === "price"
  );
  if (mine.length > 0) {
    const e = emitTesseraConceptOverlays(mine, aligned);
    boxes.push(...e.boxes);
    labels.push(...e.labels);
    priceLines.push(...e.priceLines);
    lines.push(...e.lines);
  }
  // BE indicator lines over price (MAs, bands, channels) for this chip.
  const endLabels: DrawingLabel[] = [];
  for (const entry of state.rdSeries ?? []) {
    if (entry.pane !== "price" || entry.group !== spec.key) continue;
    const specs = strategyRdSeriesSpecs(entry, state.barTimesMs, "price", true);
    series.push(...specs.series);
    priceLines.push(...specs.priceLines);
    endLabels.push(...specs.labels);
    backgrounds.push(
      ...squeezeBackgrounds(`tessera-chip-${entry.key}`, "price", state.barTimesMs, entry.squeezeOn, true)
    );
  }
  labels.push(...destackMaEndLabels(endLabels));
  // FE lines the chip asked for (MAs, Bollinger).
  const lanes = new Set(spec.lanes);
  const priceSeries = state.overlays?.price?.series;
  if (priceSeries?.length) {
    const ma = maLaneOutput(state, priceSeries, (id) => lanes.has(id));
    series.push(...ma.series);
    labels.push(...ma.labels);
    if (lanes.has("bb")) series.push(...bbLaneOutput(state, priceSeries));
  }
  if (spec.kind === "setup") {
    // Setup-bar highlight + path column washes.
    const pathHighlights = (state.pathHighlights ?? []).filter(
      (h) => h != null && Number.isFinite(h.timeMs)
    );
    const timeMs = state.highlightBarTimeMs;
    const highlights =
      pathHighlights.length > 0
        ? pathHighlights
        : timeMs != null && Number.isFinite(timeMs)
          ? [{ timeMs, direction: state.highlightDirection ?? null, label: state.highlightLabel }]
          : [];
    if (highlights.length > 0) {
      const e = emitPatternBarHighlights(highlights, aligned);
      series.push(...e.series);
      backgrounds.push(...e.backgrounds);
      labels.push(...e.labels);
    }
  }
  if (spec.kind === "entry") {
    if (state.trades?.length) {
      const e = emitTesseraTradeDrawings(state.trades, ctx.bars(), state.selectedTradeId, {
        showPositionStats: Boolean(state.showPositionStats),
        allPositionTools: Boolean(state.allPositionTools),
      });
      labels.push(...e.labels);
      lines.push(...e.lines);
      boxes.push(...e.boxes);
    }
  }
  ctx.emit({ series, labels, lines, boxes, priceLines, backgrounds });
}

function computeChipPanel(ctx: NativeIndicatorContext, type: string): void {
  const found = chipSpecByType.get(type);
  const state = readState();
  if (!found || found.panel == null || !state) {
    ctx.emit({ series: [], priceLines: [], backgrounds: [], lines: [], labels: [] });
    return;
  }
  const { spec } = found;
  const panel = spec.panels[found.panel];
  if (!panel) {
    ctx.emit({ series: [], priceLines: [], backgrounds: [], lines: [], labels: [] });
    return;
  }
  const paneId = type;
  const series: SeriesSpec[] = [];
  const priceLines: PriceLine[] = [];
  const backgrounds: Background[] = [];
  let labels: DrawingLabel[] = [];
  let lines: DrawingLine[] = [];
  let values: ReadonlyArray<number | null | undefined> = [];

  if (panel.source.kind === "be") {
    const key = panel.source.seriesKey;
    const entry = (state.rdSeries ?? []).find((e) => e.key === key);
    if (entry) {
      const specs = strategyRdSeriesSpecs(
        entry,
        state.barTimesMs,
        paneId,
        false,
        chipBarsAligned(ctx, state)
      );
      series.push(...specs.series);
      priceLines.push(...specs.priceLines);
      backgrounds.push(
        ...squeezeBackgrounds(`tessera-chip-${entry.key}`, paneId, state.barTimesMs, entry.squeezeOn, false)
      );
      values = entry.lines[0]?.values ?? entry.histogram?.values ?? [];
    }
  } else {
    const panes = state.overlays?.panes?.series;
    if (panes?.length) {
      if (panel.source.kind === "osc") {
        const lanes = new Set(panel.source.lanes);
        const osc = oscLaneOutput(state, panes, (id) => lanes.has(id), paneId);
        series.push(...osc.series);
        priceLines.push(...osc.priceLines);
        const field = OSC_VALUE_FIELD[panel.source.lanes[0] ?? "rsi"] ?? "rsi";
        values = panes.map((p) => p[field] ?? (field === "atr_pct" ? p.atr : null));
      } else {
        series.push(...macdLaneOutput(state, panes, paneId));
        values = panes.map((p) => p.macd);
      }
    }
  }

  if (panel.ownsMarkers && state.barTimesMs.length) {
    // The chip's markers for this panel (MACD "Cross", ATR rank, RVol …).
    const own = (state.conceptDrawings ?? []).filter((d) => {
      const source = (d.sourcePaneId || d.paneId || "price").trim();
      if (spec.kind === "volume") return source === "volume";
      return (
        (d.group ?? "setup") === spec.key &&
        source !== "price" &&
        source !== "volume" &&
        (d.paneId || "price").trim() !== "price"
      );
    });
    if (own.length > 0) {
      const bars = state.barTimesMs.map((t, i) => {
        const v = values[i];
        const y = v != null && Number.isFinite(Number(v)) ? Number(v) : 0;
        return { time: t, open: y, high: y, low: y, close: y, volume: 1 };
      });
      const e = emitTesseraConceptOverlays(own, bars);
      lines = e.lines;
      labels = e.labels;
    }
  }
  ctx.emit({ series, priceLines, backgrounds, lines, labels });
}

function registerChipType(type: string, title: string, overlay: boolean): void {
  registerNativeIndicator({
    type,
    title,
    shortTitle: title,
    paneHint: overlay ? "price" : "new",
    overlay,
    multiInstance: false,
    inputsSchema: emptySchema,
    defaultInputs: () => ({ revision: 0 }),
    create: (): NativeIndicator => {
      let ctx: NativeIndicatorContext | null = null;
      let unsub: (() => void) | null = null;
      const compute = () => {
        if (!ctx) return;
        if (overlay) computeChipMain(ctx, type);
        else computeChipPanel(ctx, type);
      };
      return {
        start(c) {
          ctx = c;
          unsub = activeStoreKey ? subscribeTesseraOverlayState(activeStoreKey, compute) : null;
          compute();
        },
        onBars: compute,
        onViewport() {},
        setInputs: compute,
        suspend() {},
        resume: compute,
        stop() {
          unsub?.();
          unsub = null;
          ctx = null;
        },
      };
    },
  });
}

/** Register (or refresh) every chip's indicator types; returns them in legend order. */
export function registerStrategyRdChipNatives(specs: readonly StrategyRdChipNativeSpec[]): string[] {
  const types: string[] = [];
  for (const spec of specs) {
    if (spec.mainType) {
      chipSpecByType.set(spec.mainType, { spec, panel: null });
      registerChipType(spec.mainType, spec.title, true);
      types.push(spec.mainType);
    }
    spec.panels.forEach((panel, i) => {
      chipSpecByType.set(panel.type, { spec, panel: i });
      registerChipType(panel.type, panel.title, false);
      types.push(panel.type);
    });
  }
  return types;
}
